import { useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/ui";
import { ClaimWorkCard } from "@/components/tester/claim/claim-work-card";
import { CompanyPoolPanel } from "@/components/tester/claim/company-pool-panel";

interface ClaimSearch {
  /**
   * The letter code a QR link carries, as the exact text of the URL — the
   * router's search codec reads every param as a string, so a code that looks
   * like a number arrives intact.
   */
  code?: string;
}

/** The request schema's own cap on a letter code; anything longer is not one. */
const MAX_CODE_LENGTH = 32;

export const Route = createFileRoute("/_company/claim/")({
  validateSearch: (search: Record<string, unknown>): ClaimSearch => ({
    code:
      typeof search.code === "string" && search.code.length <= MAX_CODE_LENGTH
        ? search.code
        : undefined,
  }),
  component: TesterClaimWorkPage,
});

/**
 * Claim Work — how a tester takes on work nobody assigned them.
 *
 * One card takes the tester's input, by the way in they pick from its "Claim
 * by" dropdown: the letter code printed on the letter the customer is holding
 * (the primary path — knowledge of the letter IS the customer's consent), an
 * assembly's serial number at a utility that opened that door, or an account's
 * authorization code, which links the tester's testing company to the account.
 * `ClaimWorkCard` owns the input, each way's results and refusals, and the
 * letter's reveal and claims.
 *
 * The letter's QR code opens this page as `/claim?code=K7QM4TXP`. The link
 * keeps its `code` through whatever stands between it and this page: sign-in
 * when there is no session (the shell's login redirect keeps the search), and
 * a lost connection — the shell holds the page behind its connection panel,
 * and the page mounts once the tester is back online. The card takes the code
 * on mount and has the page replace the URL without it at once — the code is
 * a bearer secret, and it leaves the address bar before the request fires.
 * The strip is the only once-guard: a remount, reload, back or restored tab
 * finds no `code` and spends no second logged attempt.
 *
 * Beside the card, the company pool counts the unclaimed jobs the tester's
 * companies offer and points to My Work.
 */
function TesterClaimWorkPage() {
  const { t } = useTranslation(["field-work"]);
  const navigate = useNavigate();
  const { code: arrivedCode } = Route.useSearch();

  const stripArrivedCode = useCallback(() => {
    void navigate({ to: "/claim", search: {}, replace: true });
  }, [navigate]);

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader title={t("field-work:claim.title")} subtitle={t("field-work:claim.subtitle")} />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1 space-y-4">
          <ClaimWorkCard arrivedCode={arrivedCode} onArrivedCodeTaken={stripArrivedCode} />

          <p className="text-xs text-muted">{t("field-work:claim.letter.note")}</p>
        </div>

        <aside className="w-full space-y-3 lg:w-72 lg:border-l lg:border-gray-200 lg:pl-6 dark:lg:border-gray-700">
          <h2 className="text-sm font-semibold text-muted">
            {t("field-work:claim.otherWays.heading")}
          </h2>
          <CompanyPoolPanel />
        </aside>
      </div>
    </div>
  );
}
